import { callFn, type InviteResult } from "@workspace/admin-ui";

export interface MembershipRow {
  membershipId: string;
  uid: string;
  role: string;
  chainId: string;
  pharmacyId: string;
  email: string;
  displayName: string;
  status: string;
  createdAt: string | null;
  suspendedAt: string | null;
  suspendedReason: string;
}

export interface InviteRow {
  id: string;
  email: string;
  role: string;
  chainId: string;
  pharmacyId: string;
  status: string;
  expiresAt: string | null;
  createdAt: string | null;
  createdByUid: string;
}

export interface AuditRow {
  id: string;
  action: string;
  actorUid: string;
  actorEmail: string;
  chainId: string;
  pharmacyId: string;
  targetType: string;
  targetId: string;
  targetLabel: string;
  timestamp: string | null;
  meta: Record<string, unknown>;
}

export const chainAdminApi = {
  createPharmacy: (data: {
    chainId: string;
    name: string;
    address: string;
    eircode: string;
    psi: string;
    id?: string;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        chainId: string;
        pharmacyId: string;
        displayName: string;
        eircode: string;
      }
    >("adminCreatePharmacy", data),
  editPharmacy: (data: {
    chainId: string;
    pharmacyId: string;
    name: string;
    address: string;
    eircode: string;
    psi: string;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        chainId: string;
        pharmacyId: string;
        displayName: string;
        eircode: string;
      }
    >("adminEditPharmacy", data),
  invitePharmacist: (data: {
    chainId: string;
    pharmacyId: string;
    email: string;
    displayName: string;
    psiNumber: string;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        uid: string;
        membershipId: string;
        email: string;
        inviteLink: string;
        emailSent: boolean;
      }
    >("adminInvitePharmacist", data),
  issuePharmacistInvite: (data: {
    chainId: string;
    pharmacyId: string;
    email: string;
    displayName: string;
    expiresDays?: number;
    psiNumber: string;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        inviteId: string;
        token: string;
        inviteLink: string;
        expiresAt: string;
        email: string;
        uid: string;
        emailSent: boolean;
      }
    >("adminIssuePharmacistInvite", data),
  listMemberships: (data: {
    chainId: string;
    pharmacyId?: string;
    status?: "active" | "suspended" | "all";
  }) =>
    callFn<typeof data, { items: MembershipRow[] }>(
      "adminListMemberships",
      data,
    ),
  listInvites: (data: {
    chainId: string;
    pharmacyId?: string;
    status?: "pending" | "accepted" | "revoked" | "expired" | "all";
  }) =>
    callFn<typeof data, { items: InviteRow[] }>("adminListInvites", data),
  revokeInvite: (data: { inviteId: string }) =>
    callFn<typeof data, { ok: true }>("adminRevokeInvite", data),
  resendInvite: (data: { inviteId: string; expiresDays?: number }) =>
    callFn<typeof data, { ok: true; email: string } & InviteResult>(
      "adminResendInvite",
      data,
    ),
  revokeMembership: (data: {
    membershipId?: string;
    uid?: string;
    email?: string;
    reason?: string;
    disableAuth?: boolean;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        uid: string;
        membershipId: string;
        historyId: string;
        authDisabled: boolean;
        authError: string | null;
      }
    >("adminRevokeMembership", data),
  suspendMembership: (data: {
    membershipId?: string;
    uid?: string;
    email?: string;
    reason?: string;
  }) =>
    callFn<typeof data, { ok: true; uid: string; membershipId: string }>(
      "adminSuspendMembership",
      data,
    ),
  reactivateMembership: (data: {
    membershipId?: string;
    uid?: string;
    email?: string;
  }) =>
    callFn<typeof data, { ok: true; uid: string; membershipId: string }>(
      "adminReactivateMembership",
      data,
    ),
  transferMembership: (data: {
    membershipId?: string;
    uid?: string;
    email?: string;
    toChainId: string;
    toPharmacyId: string;
    reason?: string;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        uid: string;
        fromMembershipId: string;
        toMembershipId: string;
        historyId: string;
        repointed: boolean;
      }
    >("adminTransferMembership", data),
  updatePharmacist: (data: {
    membershipId: string;
    displayName?: string;
    psiNumber?: string;
  }) =>
    callFn<typeof data, { ok: true; membershipId: string }>(
      "adminUpdatePharmacist",
      data,
    ),
  invitePharmacyAdmin: (data: {
    chainId: string;
    pharmacyId: string;
    email: string;
    displayName: string;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        uid: string;
        membershipId: string;
        email: string;
        inviteLink: string;
        emailSent: boolean;
        isNewAccount: boolean;
      }
    >("adminInvitePharmacyAdmin", data),
  listPharmacyAdmins: (data: {
    chainId: string;
    pharmacyId: string;
    status?: "active" | "suspended" | "all";
  }) =>
    callFn<typeof data, { items: MembershipRow[] }>("adminListMemberships", {
      ...data,
      pharmacyId: data.pharmacyId,
    }),
  listAuditLog: (data: {
    limit?: number;
    action?: string;
    fromTs?: string | number;
    toTs?: string | number;
  }) => callFn<typeof data, { items: AuditRow[] }>("adminListAuditLog", data),
  chainAdminBookingsOverview: (data: { chainId: string }) =>
    callFn<
      typeof data,
      {
        items: Array<{
          pharmacyId: string;
          displayName: string;
          hasBookingSetup: boolean;
          bookingPharmacyCount: number;
          bookingPharmacyId: string | null;
          roomCount: number;
          upcomingCount: number;
        }>;
        totalUpcoming: number;
      }
    >("chainAdminBookingsOverview", data),
  chainAdminListBookingPharmacies: (data: {
    chainId: string;
    pharmacyId: string;
  }) =>
    callFn<
      typeof data,
      {
        items: Array<{
          id: string;
          name: string;
          address: string;
          eircode: string;
          // True for the oldest booking-pharmacy record — the one every
          // other surface treats as canonical when duplicates exist.
          isPrimary?: boolean;
        }>;
      }
    >("chainAdminListBookingPharmacies", data),
  chainAdminListRooms: (data: { chainId: string; bookingPharmacyId: string }) =>
    callFn<
      typeof data,
      {
        items: Array<{
          id: string;
          name: string;
          description: string;
          isActive: boolean;
        }>;
      }
    >("chainAdminListRooms", data),
  chainAdminListAvailabilityPharmacists: (data: {
    chainId: string;
    bookingPharmacyId: string;
  }) =>
    callFn<
      typeof data,
      { items: Array<{ pharmacistId: string; displayName: string }> }
    >("chainAdminListAvailabilityPharmacists", data),
  chainAdminGetAvailability: (data: {
    chainId: string;
    bookingPharmacyId: string;
    pharmacistId: string;
  }) =>
    callFn<
      typeof data,
      {
        availability: null | {
          windows: Array<{
            dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6;
            startTime: string;
            endTime: string;
          }>;
          blockoutDates: Array<{ date: string; reason?: string }>;
        };
      }
    >("chainAdminGetAvailability", data),
  chainAdminSetAvailability: (data: {
    chainId: string;
    bookingPharmacyId: string;
    pharmacistId: string;
    windows: Array<{ dayOfWeek: 0 | 1 | 2 | 3 | 4 | 5 | 6; startTime: string; endTime: string }>;
    blockoutDates: Array<{ date: string; reason?: string }>;
  }) =>
    callFn<typeof data, { ok: true }>("chainAdminSetAvailability", data),
  chainAdminListBookings: (data: {
    chainId: string;
    bookingPharmacyId: string;
    status?:
      | "all"
      | "pending"
      | "confirmed"
      | "cancelled"
      | "rescheduled"
      | "completed"
      | "no_show";
    pharmacistId?: string;
    startDate?: string;
    endDate?: string;
  }) =>
    callFn<
      typeof data,
      {
        items: Array<{
          id: string;
          roomId: string;
          pharmacistId: string | null;
          condition: string | null;
          date: string;
          startTime: string;
          endTime: string;
          status: string;
          patientEmail: string;
          createdAt: string | null;
          updatedAt: string | null;
        }>;
      }
    >("chainAdminListBookings", data),
  deletePharmacy: (data: { chainId: string; pharmacyId: string }) =>
    callFn<
      typeof data,
      { ok: true; chainId: string; pharmacyId: string; displayName: string }
    >("adminDeletePharmacy", data),
  exportPatientData: (data: {
    chainId?: string;
    patientName: string;
    patientDOB: string;
    consultationIds?: string[];
  }) =>
    callFn<typeof data, PatientExportResult>("exportPatientData", data),
  deletePatientData: (data: {
    chainId?: string;
    patientName: string;
    patientDOB: string;
    consultationIds?: string[];
  }) =>
    callFn<typeof data, PatientDeleteResult>("deletePatientData", data),
};

// Rooms & availability are read-only here — pharmacists set them
// exclusively from the mobile app's Bookings tab. There are intentionally
// no addRoom/updateRoom/setRoomAvailability methods.
export const bookingAdminApi = {
  updatePharmacy: (data: {
    chainId: string;
    pharmacyId: string;
    name: string;
    address: string;
    eircode: string;
    primaryColor?: string;
    logoUrl?: string;
  }) =>
    callFn<
      typeof data,
      {
        ok: true;
        id: string;
        name: string;
        address: string;
        eircode: string;
        primaryColor: string;
        logoUrl: string;
      }
    >("adminUpdateBookingPharmacy", data),
};

export interface PatientExportResult {
  ok: boolean;
  found: number;
  fileCount: number;
  auditRowCount: number;
  signedUrl: string | null;
  expiresInSeconds: number;
}

export interface PatientDeleteResult {
  ok: boolean;
  found: number;
  filesDeleted: number;
  exportBundlesDeleted: number;
  failures: string[];
  results: Array<{
    consultationId: string;
    filesDeleted: number;
    indexRemoved: boolean;
    error: string | null;
  }>;
}
